import { ReactElement, useEffect, useMemo } from 'react';
import { Typography } from '@mui/material';

import { useGetUnstakePeriodQuery } from 'modules/api/actions/getUnstakePeriod';
import { mapDataToUndefinedIfSkip } from 'modules/api/utils';
import { GuardButton } from 'modules/auth/components/GuardButton';
import { useConnection } from 'modules/auth/hooks/useConnection';
import successImage from 'modules/common/assets/stake-success.png';
import { KnownDialogs, useDialog } from 'modules/dialogs';
import { useTranslation } from 'modules/i18n';
import { useClaimUnstakeMutation } from 'modules/liquidStaking/actions/claimUnstake';
import {
  EMPTY_POOL_UNSTAKES,
  useGetLSPendingUnstakesQuery,
} from 'modules/liquidStaking/actions/getPendingUnstakes';
import { useLiquidStakingPool } from 'modules/liquidStaking/hooks/useLiquidStakingPool';
import { getClaimTimeLeft } from 'modules/pool/utils/getClaimTimeLeft';

import { ClaimTable } from '../ClaimTable';
import { translation } from './translation';
import { useStyles } from './useStyles';

export function Claim(): ReactElement {
  const { classes } = useStyles();
  const { t, keys } = useTranslation(translation);

  const { isConnected, connectionKey, isSafeAccount, cacheTime } =
    useConnection();
  const { onOpen } = useDialog(KnownDialogs.claimSuccess);

  const { poolAddress } = useLiquidStakingPool();
  const { data: poolUnstakes = EMPTY_POOL_UNSTAKES } =
    useGetLSPendingUnstakesQuery(
      { connectionKey, poolAddress },
      {
        skip: !isConnected,
        selectFromResult: mapDataToUndefinedIfSkip,
        refetchOnMountOrArgChange: cacheTime,
      },
    );

  const { data: unstakePeriodDays } = useGetUnstakePeriodQuery(
    { poolAddress },
    { refetchOnMountOrArgChange: cacheTime },
  );

  const activeUnstakes = useMemo(() => {
    if (!unstakePeriodDays) {
      return [];
    }

    return poolUnstakes.filter(
      unstake => !getClaimTimeLeft(unstake.executedAt, unstakePeriodDays),
    );
  }, [poolUnstakes, unstakePeriodDays]);

  const [claim, { data, isLoading }] = useClaimUnstakeMutation();

  useEffect(() => {
    if (data && !isSafeAccount) {
      onOpen();
    }
  }, [data, onOpen, isSafeAccount]);

  if (activeUnstakes?.length) {
    return (
      <div>
        <ClaimTable pendingUnstakes={activeUnstakes} />

        <GuardButton
          fullWidth
          loading={isLoading}
          size="large"
          sx={theme => ({ marginTop: theme.spacing(6) })}
          type="submit"
          variant="contained"
          onClick={() => claim({ poolAddress })}
        >
          {t(keys.claim)}
        </GuardButton>
      </div>
    );
  }

  return (
    <div className={classes.empty}>
      <div className={classes.content}>
        <img alt="" className={classes.image} src={successImage} />

        <Typography component="div" textAlign="center" variant="h5">
          {t(keys.empty)}
        </Typography>
      </div>

      <GuardButton isConnectedOnly size="large" variant="outlined" />
    </div>
  );
}
